import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { useEffect, useMemo } from "react";

import { useParams } from "next/navigation";

import { useUser } from "@/contexts/user-provider";

import { hasGeneratedAnswers } from "@/lib/note-utils";
import { PERMISSIONS, hasPermission } from "@/lib/permissions";

import { CreateNoteState, NoteType } from "@/types/enums/note-state";

import {
  noteQueryKeys,
  noteQueryOptions,
} from "@/api/provider/scribe/note-query";
import { toggleNoteTemplateFavorite } from "@/api/provider/scribe/notes";
import { useScribeNote, useScribeRecording } from "@/stores/scribe-store";

export function useSelectedNote() {
  const { setNoteState } = useScribeNote();
  const queryClient = useQueryClient();
  const { currentUser } = useUser();
  // Either permission is sufficient — the backend's per-note read endpoints
  // accept CLINICAL_NOTES_READ or PATIENTS_NOTES_WRITE so a write-only user
  // can still edit a note they authored.
  const canAccessNote =
    hasPermission(currentUser, PERMISSIONS.CLINICAL_NOTES_READ) ||
    hasPermission(currentUser, PERMISSIONS.PATIENTS_NOTES_WRITE);

  const { id: noteId }: { id: string } = useParams();

  const {
    data: selectedNote,
    isPending,
    isLoading,
    isError,
    error,
  } = useQuery({
    ...noteQueryOptions.detail(noteId ? noteId : ""),
    enabled:
      !!noteId && !isLocalNoteId(noteId) && isNoteId(noteId) && canAccessNote,
  });

  // Centralize updates that depend on selectedNote
  useEffect(() => {
    if (!selectedNote) return;

    const parentNoteId = selectedNote.parentNoteId;
    const currentVersion = selectedNote.currentVersion;
    if (parentNoteId && currentVersion) {
      queryClient.setQueryData(
        noteQueryKeys.revision({ parentNoteId, version: currentVersion }),
        selectedNote,
      );
    }

    // Check if note is complete: has answers (for form-based) or has content (legacy)
    if (hasGeneratedAnswers(selectedNote)) {
      setNoteState(CreateNoteState.COMPLETE);
    } else {
      setNoteState(CreateNoteState.IDLE);
    }
  }, [selectedNote, queryClient]);

  const noteType = useMemo(() => {
    if (selectedNote?.aiGenerated) {
      return NoteType.AI;
    }

    // If note has source documents but also has content/answers, treat as completed AI note
    // Necessary for uploaded notes which are not marked as AI generated
    if ((selectedNote?.sourceDocuments?.length || 0) > 0) {
      return hasGeneratedAnswers(selectedNote) ? NoteType.AI : NoteType.UPLOAD;
    }

    return NoteType.MANUAL;
  }, [selectedNote]);

  return {
    selectedNote,
    isPending,
    isLoading,
    isError,
    error,
    noteType,
  };
}

export function useNoteTemplates() {
  const { currentUser } = useUser();
  const queryClient = useQueryClient();
  const { data, isPending, isError, error } = useQuery(
    noteQueryOptions.templates(),
  );

  // Filter templates by user access and sort favorites first
  // null/undefined userIds = everyone has access (legacy), empty array = no one has access
  const templates = useMemo(() => {
    if (!data || !currentUser) return [];
    const filtered = data.filter(
      (template) =>
        !template.userIds || template.userIds.includes(currentUser.id),
    );
    // Sort: favorites first, then alphabetically by name
    return filtered.sort((a, b) => {
      if (a.isFavorited && !b.isFavorited) return -1;
      if (!a.isFavorited && b.isFavorited) return 1;
      return a.name.localeCompare(b.name);
    });
  }, [data, currentUser]);

  // Mutation to toggle favorite status with optimistic updates.
  // Scoped so toggles run serially: a rapid double-click on the same star
  // would otherwise fire two overlapping POSTs (insert then delete) whose
  // responses can arrive out of order, leaving the cache stuck on the older
  // state with no refetch to repair it. Serial execution keeps the optimistic
  // flips, cache writes, and server writes in lockstep order.
  const toggleFavoriteMutation = useMutation({
    scope: { id: "toggle-note-template-favorite" },
    mutationFn: toggleNoteTemplateFavorite,
    onMutate: async (templateId: string) => {
      // Cancel any outgoing refetches to prevent race conditions
      await queryClient.cancelQueries({ queryKey: noteQueryKeys.templates() });

      // Snapshot the previous value for rollback
      const previousTemplates = queryClient.getQueryData(
        noteQueryKeys.templates(),
      );

      // Optimistically update the cache
      queryClient.setQueryData(
        noteQueryKeys.templates(),
        (oldData: typeof data) => {
          if (!oldData) return oldData;
          return oldData.map((template) =>
            template.id === templateId
              ? { ...template, isFavorited: !template.isFavorited }
              : template,
          );
        },
      );

      // Return context with the previous value
      return { previousTemplates };
    },
    onSuccess: (updatedTemplate, _templateId, context) => {
      // requestData swallows HTTP errors and resolves with null. Treat a null
      // response as a failure and roll the optimistic flip back.
      if (!updatedTemplate) {
        if (context?.previousTemplates) {
          queryClient.setQueryData(
            noteQueryKeys.templates(),
            context.previousTemplates,
          );
        }
        return;
      }
      // Write the authoritative response (built from the primary DB) straight
      // into the cache instead of invalidating. The list/detail GET endpoints
      // read from the replica (ReadOnlyStoreSession), which can lag this
      // just-committed write and clobber the correct favorite state — so a
      // refetch here would revert the toggle under replication lag.
      queryClient.setQueryData(
        noteQueryKeys.templates(),
        (oldData: typeof data) =>
          oldData?.map((template) =>
            template.id === updatedTemplate.id ? updatedTemplate : template,
          ),
      );
      queryClient.setQueryData(
        noteQueryKeys.templateDetail(updatedTemplate.id),
        updatedTemplate,
      );
    },
    onError: (_err, _templateId, context) => {
      // Rollback to the previous value on error
      if (context?.previousTemplates) {
        queryClient.setQueryData(
          noteQueryKeys.templates(),
          context.previousTemplates,
        );
      }
    },
  });

  const toggleFavorite = (templateId: string) => {
    toggleFavoriteMutation.mutate(templateId);
  };

  return {
    templates,
    isPending,
    isError,
    error,
    toggleFavorite,
    isTogglingFavorite: toggleFavoriteMutation.isPending,
    favoriteError: toggleFavoriteMutation.error,
  };
}

export function useTemplateDetail(templateId?: string) {
  const { data: templateDetail, ...rest } = useQuery({
    ...noteQueryOptions.templateDetail(templateId ?? ""),
    enabled: !!templateId,
  });
  return { templateDetail, ...rest };
}

export function isLocalNoteId(id?: string) {
  return !!id && id.startsWith("local_id");
}

export function isNoteId(id?: string) {
  return !!id && id.startsWith("not");
}

// Recording-scoped version of selected note, based on currentRecordingId
export function useRecordingNote() {
  const { currentRecordingId } = useScribeRecording();
  const { currentUser } = useUser();
  const canAccessNote =
    hasPermission(currentUser, PERMISSIONS.CLINICAL_NOTES_READ) ||
    hasPermission(currentUser, PERMISSIONS.PATIENTS_NOTES_WRITE);
  const { data, isPending, isError, error } = useQuery({
    ...noteQueryOptions.detail(currentRecordingId ? currentRecordingId : ""),
    enabled:
      !!currentRecordingId &&
      !isLocalNoteId(currentRecordingId) &&
      canAccessNote,
  });

  return { recordingNote: data, isPending, isError, error };
}
